import { fetchToken } from "./fetch.authentication";
import { jwtDecode } from "jwt-decode";
import { ErrorType, parseError } from "../types/Error/Errors.ts";
import {
  AuthTokenPayload,
  SessionTokenPayload,
  UserAuthentication,
  UserSession,
} from "../types/User.ts";
import { Result } from "../types/Result.ts";
import { API_URL } from "../const/enviroment.ts";
import { safeLocalStorage } from "../utils/safeLocalStroage.ts";

export async function resolvePayload<T>(
  res: Response,
): Promise<Result<T, ErrorType>> {
  const payload = await res.json();
  if (!res.ok) {
    const error = parseError(payload);
    return { ok: false, error: error };
  }
  return { ok: true, value: payload as T };
}

export const fetcher = (input: RequestInfo | URL, init?: RequestInit) =>
  fetch(input, init).then((res) => {
    if (!res.ok) {
      throw new Error(`HTTP error! ${res.statusText}: ${res.status}.`);
    }
    return res.json();
  });

export const authFetcher = (input: RequestInfo | URL, init?: RequestInit) =>
  fetchWithAuth(input, init).then((res) => {
    if (!res.ok) {
      throw new Error(`HTTP error! ${res.statusText}: ${res.status}.`);
    }
    return res.json();
  });

export const fetchWithAuth = async (
  input: RequestInfo | URL,
  options: RequestInit = {},
) => {
  // We want to make sure to check for internet before refetching => when the next request fails, it has some more serious error
  if (!navigator.onLine)
    throw new Error("Dein Gerät scheint nicht online zu sein.");

  const auth = getAuthToken();
  const session = getSession();

  const token = await refetchSessionToken(auth, session);
  if (!token.ok) {
    console.warn("Session invalid. Autoremoving");
    clearSession();
    setTimeout(() => window.location.reload(), 5000);
    throw new Error(
      "Deine Anmeldung konnten nicht fortgesetzt werden. Aus Sicherheitsgründen wurden die Daten gelöscht und musst dich leider neu anmelden. Die Seite wir gleich neu geladen.",
    );
  }

  const isFormData = options.body instanceof FormData;

  const headers: HeadersInit = {
    ...options.headers,
    Authorization: token ? `Bearer ${token.token}` : "",
    ...(isFormData ? {} : { "Content-Type": "application/json" }),
  };
  try {
    return fetch(input, { ...options, headers });
  } catch {
    throw new Error("Netzwerkfehler. Überprüfe deine Verbindung.");
  }
};

export const validSession = (session: UserSession): boolean => {
  return session.ok && session.exp > Date.now() / 1000 + 600; //and longer valid then 10min)
};
let refreshPromise: Promise<Result<string, string>> | null = null;
export const refetchSessionToken = async (
  auth: UserAuthentication,
  session: UserSession,
  force: boolean = false,
): Promise<UserSession> => {
  if (!auth.ok) {
    console.warn("Session not ok.");
    clearSession();
    return { ok: false };
  }
  const authData = jwtDecode(auth.token) as AuthTokenPayload;
  if (!authData || authData.exp < Date.now() / 1000) {
    console.log("Session expired.");
    clearSession();
    //window.location.reload();
    return { ok: false };
  }
  if (session && !force && validSession(session)) {
    // If the session is valid for longer then 10min.
    return session;
  }
  // else we request a new one.
  try {
    if (!refreshPromise) {
      refreshPromise = fetchToken(auth.token).finally(() => {
        refreshPromise = null;
      });
    }
    const res = await refreshPromise;
    if (!res.ok) {
      console.error("Request failed.");
      clearSession();
      return { ok: false };
    }
    return setSessionToken(res.value);
  } catch (e) {
    console.error(e);
    clearSession();
    return { ok: false };
  }
};

export const getSession = (): UserSession => {
  try {
    const raw = safeLocalStorage.getItem("sessionToken");
    if (!raw) return { ok: false };
    const session = JSON.parse(raw) as UserSession;
    if (validSession(session)) {
      return session;
    }
  } catch (e) {
    console.error(e);
  }
  return { ok: false };
};

export const getAuthToken = (): UserAuthentication => {
  const authToken = safeLocalStorage.getItem("authToken");
  if (!authToken) {
    console.log("No session found.");
    clearSession();
    return { ok: false };
  }
  //We make sure it is parsed correctly
  const auth = parseSessionToken(authToken);
  if (!auth || !auth.ok) {
    console.log("Not logged in.");
    clearSession();
    return { ok: false };
  }
  return auth;
};

export const clearSession = () => {
  safeLocalStorage.removeItem("authToken"); // Clear token
  safeLocalStorage.removeItem("sessionToken"); // Clear token
};

export const parseSessionToken = (authToken: string) => {
  try {
    return JSON.parse(authToken) as UserAuthentication;
  } catch (e) {
    console.error(e);
    return null;
  }
};

export const setSessionToken = (token: string): UserSession => {
  try {
    const decode = jwtDecode(token) as SessionTokenPayload;
    const newSess = {
      ok: true,
      token: token,
      ...decode,
    };
    safeLocalStorage.setItem("sessionToken", JSON.stringify(newSess));
    return newSess;
  } catch (e) {
    console.error(e);
    return { ok: false };
  }
};

export const DEFAULT_FILE_URL: string = API_URL;
export const DEFAULT_API_URL: string = API_URL + "/api";

// AdminRoutes: Used for getting admin related stuff
export const ADMIN_DELETE_USER_URL = (userId: string) =>
  DEFAULT_API_URL + `/admin/user/${userId}`;
export const ADMIN_GET_USERS = DEFAULT_API_URL + "/admin/users";
export const ADMIN_DELETE_ITEM = (id: string) =>
  DEFAULT_API_URL + `/admin/item/${id}`;

export const CATEGORY_URL: string = DEFAULT_API_URL + "/category/";
export const CATEGORY_PARENT_URL: string = CATEGORY_URL + "parent/";

export const ITEM_URL: string = DEFAULT_API_URL + "/items/";
export const API_GET_ITEM = (itemId: string) => ITEM_URL + itemId;
export const USER_URL: string = DEFAULT_API_URL + "/user/";
export const CHAT_URL: string = DEFAULT_API_URL + "/chat/";
export const CHAT_PEEK_URL: string = CHAT_URL + "peek/";
export const CHAT_UNREAD_URL: string = CHAT_URL + "unread";

export const MESSAGE_URL: string = DEFAULT_API_URL + "/message/";
export const AUTH_ENDPOINT = "/auth";
export const LOGIN_URL: string = DEFAULT_API_URL + AUTH_ENDPOINT + "/login/";
export const DEV_URL: string = DEFAULT_API_URL + AUTH_ENDPOINT + "/dev/";
export const VERIFY_URL: string = DEFAULT_API_URL + AUTH_ENDPOINT + "/verify/";
export const RESET_URL: string = DEFAULT_API_URL + AUTH_ENDPOINT + "/reset/";
export const CHALLENGE_URL = DEFAULT_API_URL + AUTH_ENDPOINT + "/challenge";
export const REGISTER_URL: string =
  DEFAULT_API_URL + AUTH_ENDPOINT + "/register/";
export const DELETE_URL: string = DEFAULT_API_URL + AUTH_ENDPOINT + "/delete/";

export const TOKEN_URL: string = DEFAULT_API_URL + AUTH_ENDPOINT + "/token/";
export const EMAIL_URL: string = DEFAULT_API_URL + AUTH_ENDPOINT + "/email/";
export const IMAGE_URL: string = DEFAULT_API_URL + "/image/";
export const FILTER_URL: string = DEFAULT_API_URL + "/filter/";

export const REPORT_URL: string = DEFAULT_API_URL + "/report/";
